/**
 * @name degreeService.ts
 * @version 2.0.0
 * @description Servicio para gestión de títulos universitarios
 * @author
 * @changelog
 * 2.0.0 - 2024/12/19 - @santiago - Refactorización para eliminar código repetido
 * 1.0.0 - 2024/12/19 - @santiago - Cambio inicial
 */

import { apiService } from './apiService'
import { ref as dbRef, get, query, orderByChild, equalTo, update } from 'firebase/database'
import { database } from '@/firebase/config'
import { DocumentService } from './documentService'
import { ETipoError, IBaseDocument, IDegree } from '@shared/types'
import { getAuth } from 'firebase/auth'
import JSZip from 'jszip'

export interface DegreeDocument extends IBaseDocument {
	studentName: string
	studentId: string
	degree: string
	graduationDate: string
}

export class DegreeService extends DocumentService {
	private institutionId: string

	constructor() {
		super('/api/degrees')
		this.institutionId = ''
	}

	public setInstitutionId(id: string): void {
		this.institutionId = id
	}

	public async getDegrees(): Promise<IDegree[]> {
		const documents = await this.getDocuments()
		return documents as IDegree[]
	}

	public async validateDegree(degreeId: string): Promise<IDegree> {
		const document = await this.validateDocument(degreeId)
		return document as IDegree
	}

	public async searchByStudent(studentId: string): Promise<IDegree[]> {
		return this._handleApiCall(() => apiService.get(`search/student/${studentId}`), 'searchByStudent')
	}

	/**
	 * @description Descarga la plantilla de documentos
	 * @param institutionId ID de la institución
	 * @param type Tipo de documento ('degree' o 'certificate')
	 * @param testMode Si está en modo de prueba
	 * @returns Promise con el archivo de la plantilla
	 */
	public async downloadTemplate(institutionId: string, type: 'degree' | 'certificate' = 'degree', testMode: boolean = false): Promise<Blob> {
		const response = await apiService.post(
			'downloadTemplate',
			{
				institution: institutionId,
				type,
				testMode
			},
			{
				responseType: 'arraybuffer'
			}
		)

		return this._createAndDownloadFile(response, 'Titulos.xlsx', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet')
	}

	/**
	 * @description Crea un paquete de títulos
	 * @param degrees Mapa de títulos a incluir en el paquete
	 * @param institutionId ID de la institución
	 * @param name Nombre del paquete
	 * @param testMode Si está en modo de prueba
	 * @returns Promise con la respuesta del servidor
	 */
	public async createPackage(degrees: Record<string, boolean>, institutionId: string, name: string, testMode: boolean = false): Promise<any> {
		const db = database
		const testPath = testMode ? '/testMode' : ''
		const path = `/institutions/${institutionId}${testPath}/degrees/queue`

		// Verificar cada certificado
		for (const degreeId of Object.keys(degrees)) {
			const degreeRef = dbRef(db, `${path}/${degreeId}`)
			const snapshot = await get(degreeRef)

			if (!snapshot.exists()) {
				throw new Error(`El título ${degreeId} no existe`)
			}

			const degreeData = snapshot.val()
			console.log('degreeData', degreeData)
			if (!degreeData.success) {
				throw new Error(`El título ${degreeId} no ha sido validado correctamente`)
			}
		}

		return apiService.post('createPackage', {
			degrees,
			institution: institutionId,
			name,
			testMode
		})
	}

	/**
	 * @description Elimina un paquete de títulos
	 * @param packageId ID del paquete
	 * @param institutionId ID de la institución
	 * @param testMode Si está en modo de prueba
	 * @returns Promise con la respuesta del servidor
	 */
	public async deletePackage(packageId: number, institutionId: string, testMode: boolean = false): Promise<any> {
		return apiService.post('deletePackage', {
			institution: institutionId,
			package: packageId,
			testMode
		})
	}

	/**
	 * @description Busca títulos por diferentes criterios
	 * @param type Tipo de búsqueda (folio, numero, curp)
	 * @param value Valor a buscar
	 * @param institutionId ID de la institución
	 * @param testMode Si está en modo de prueba
	 * @returns Promise con los resultados de la búsqueda
	 */
	public async searchDegrees(type: 'folio' | 'numero' | 'curp', value: string, institutionId: string, testMode: boolean = false): Promise<any[]> {
		const path = this._buildFirebasePath(institutionId, testMode)
		const degreesRef = dbRef(database, path)

		const searchQuery = this._buildSearchQuery(degreesRef, type, value)
		const snapshot = await get(searchQuery)

		return this._mapFirebaseResults(snapshot)
	}

	/**
	 * @description Envía un paquete a la SEP
	 * @param params Parámetros para enviar a la SEP
	 * @returns Promise con la respuesta
	 */
	public async sendDegreesPackageToSEP(params: { institution: string; package: number; testMode?: boolean }): Promise<any> {
		return this._handleApiCall(() => apiService.post('sendDegreesPackageToSEP', params), 'sendDegreesPackageToSEP')
	}

	/**
	 * @description Obtiene la respuesta de la SEP para un paquete
	 * @param params Parámetros para obtener la respuesta
	 * @returns Promise con la respuesta
	 */
	public async getResponseDegreeSEP(params: { institution: string; package: number; testMode?: boolean }): Promise<any> {
		return this._handleApiCall(() => apiService.post('getResponseDegreeSEP', params), 'getResponseDegreeSEP')
	}

	/**
	 * @description Solicita autorización para firmar un paquete
	 * @param params Parámetros para solicitar autorización
	 * @returns Promise con la respuesta
	 */
	public async signAuthorization(params: { responsable: number; institution: string; package: number; code: string; testMode?: boolean }): Promise<any> {
		return this._handleApiCall(() => apiService.post('degrees/signAuthorization', params), 'signAuthorization')
	}

	/**
	 * @description Envía código para firmar a un responsable
	 * @param params Parámetros para enviar código
	 * @returns Promise con la respuesta
	 */
	public async sendCodeForSignResponsable(params: { responsable: number; institution: string; package: number }): Promise<any> {
		return this._handleApiCall(() => apiService.post('degrees/sendCodeForSignResponsable', params), 'sendCodeForSignResponsable')
	}

	/**
	 * @description Obtiene los detalles de los títulos de un paquete específico
	 * @param packageId ID del paquete
	 * @param institutionId ID de la institución
	 * @param testMode Si está en modo de prueba
	 * @returns Promise con un array de títulos del paquete
	 */
	public async getPackageDegrees(packageId: number, institutionId: string, testMode: boolean = false): Promise<IDegree[]> {
		const path = this._buildFirebasePath(institutionId, testMode)
		const degreesRef = dbRef(database, path)
		const packageQuery = query(degreesRef, orderByChild('package'), equalTo(packageId))

		const snapshot = await get(packageQuery)

		if (snapshot.exists()) {
			const degreesData = snapshot.val()
			return Object.entries(degreesData).map(([id, data]: [string, any]) => ({
				id,
				...(data as Partial<IDegree>),
				data: data.data ?? { Profesionista: {}, Carrera: {} },
				cadenaOriginal: data.cadenaOriginal,
				folioControl: data.folioControl
			})) as IDegree[]
		}
		return []
	}

	/**
	 * @description Envía las firmas de los documentos al backend
	 * @param institutionId ID de la institución
	 * @param face Base64 de la foto del rostro
	 * @param signatureFace Firma de la foto del rostro
	 * @param deviceId ID del dispositivo
	 * @param degrees Array de títulos con sus firmas
	 * @param testMode Si está en modo de prueba
	 * @returns Promise con la respuesta del servidor
	 */
	public async addSignatures(institutionId: string, face: string, signatureFace: string, deviceId: string, degrees: any[], testMode: boolean = false): Promise<void> {
		try {
			await apiService.post('addSignatures', {
				institution: institutionId,
				face,
				signatureFace,
				deviceId,
				degrees,
				testMode
			})
		} catch (error: any) {
			console.error('Error adding signatures:', error)
			throw new Error(error.message || 'Error al agregar las firmas')
		}
	}

	/**
	 * @description Cancela un título en la SEP
	 * @param institutionId ID de la institución
	 * @param folioControl Folio de control del título
	 * @param testMode Si está en modo de prueba
	 * @returns Promise con la respuesta del servidor
	 */
	public async cancelDegree(institutionId: string, folioControl: string, testMode: boolean = false): Promise<any> {
		return this._handleApiCall(
			() =>
				apiService.post('cancelDegreeSEP', {
					institution: institutionId,
					folioControl,
					testMode
				}),
			'cancelDegree'
		)
	}

	public async uploadAuthenticatedXml(institutionId: string, folioControl: string, testMode: boolean = false, b64Xml: string): Promise<any> {
		return apiService.post('uploadAuthenticatedXML', {
			idInstitution: institutionId,
			folioControl: folioControl,
			testMode: testMode,
			b64Xml: b64Xml
		})
	}
	public async degreeToPdf(institutionId: string, folioControl: string): Promise<any> {
		return apiService.post('degreeToPDF', {
			idInstitution: institutionId,
			folioControl: folioControl
		})
	}

	/**
	 * @description Cancela todos los títulos de un paquete en la SEP
	 * @param institutionId ID de la institución
	 * @param packageId ID del paquete
	 * @param testMode Si está en modo de prueba
	 * @returns Promise con la respuesta del servidor
	 */
	public async cancelPackageDegrees(institutionId: string, packageId: number, testMode: boolean = false): Promise<any> {
		const path = this._buildFirebasePath(institutionId, testMode)
		const degreesRef = dbRef(database, path)
		const packageQuery = query(degreesRef, orderByChild('package'), equalTo(packageId))

		const snapshot = await get(packageQuery)
		if (snapshot.exists()) {
			const degreesInPackage = snapshot.val()
			const degreesArray = Object.values(degreesInPackage) as any[]

			// Procesar todos los títulos y recolectar resultados
			const results = []
			for (const currentDegree of degreesArray) {
				try {
					const result = await this.cancelDegree(institutionId, currentDegree.folioControl, testMode)
					results.push(result)
				} catch (error: any) {
					results.push({ code: -1, message: error.message || 'Error al cancelar título' })
				}
			}

			// Analizar resultados
			const successfulCancellations = results.filter(r => r.code === 1).length
			const alreadyCanceled = results.filter(r => r.code === 2).length
			const errors = results.filter(r => r.code === -1).length
			const totalDegrees = degreesArray.length

			// Determinar mensaje apropiado
			if (errors > 0) {
				throw new Error(`Error al cancelar ${errors} de ${totalDegrees} títulos del paquete`)
			} else if (alreadyCanceled === totalDegrees) {
				return { code: 2, message: 'Todos los títulos del paquete ya estaban cancelados previamente' }
			} else if (alreadyCanceled > 0) {
				return {
					code: 1,
					message: `Paquete procesado: ${successfulCancellations} títulos cancelados, ${alreadyCanceled} ya estaban cancelados previamente`
				}
			} else {
				return { code: 1, message: `Todos los títulos del paquete (${totalDegrees}) fueron cancelados exitosamente` }
			}
		}
		return Promise.resolve({ code: 1, message: 'No se encontraron títulos en el paquete' })
	}

	public async saveData(degree: IDegree, institution: number, testMode: boolean = false, isEdit: boolean = false): Promise<void> {
		try {
			if (!isEdit && degree.id === '') {
				degree.id = new Date().getTime().toString()
			}

			await this._validateUserPermissions(institution)

			const testPath = testMode ? '/testMode' : ''
			const path = `/institutions/${institution}${testPath}/degrees/queue/${degree.id}`

			await this._validateDegreeNotInPackage(path)

			const updates: { [key: string]: any } = {
				[`${path}/data`]: degree.data
			}
			if (isEdit) {
				updates[`${path}/date`] = new Date().getTime()
			}
			await update(dbRef(database), updates)
		} catch (error) {
			console.error('Error al guardar el titulo:', error)
			throw new Error('Error al guardar el titulo')
		}
	}

	protected getInstitutionPath(path: string): string {
		return `institutions/${this.institutionId}${path}`
	}

	public async getInstitutionName(institutionId: string): Promise<string> {
		const snapshot = await get(dbRef(database, `institutions/${institutionId}/nombreInstitucion`))
		const name = snapshot.val()
		return name
	}

	public async getZipOfPackage(params: { package: number; institution: string; testMode?: boolean; name?: string }): Promise<any> {
		return this._generateZipFromDocuments({
			...params,
			type: 'package',
			getDocuments: () => this._getPackageDocuments(params.package, params.institution, params.testMode),
			generateFileName: (documents: any) => this._generatePackageFileName(documents, params.name)
		})
	}

	public async getZipOfDegree(params: { degree: string; institution: string; testMode?: boolean }): Promise<any> {
		return this._generateZipFromDocuments({
			...params,
			type: 'degree',
			getDocuments: () => this._getDegreeDocument(params.degree, params.institution, params.testMode),
			generateFileName: (documents: any) => this._generateDegreeFileName(documents)
		})
	}

	/**
	 * @description Genera un archivo ZIP a partir de documentos con lógica común
	 */
	private async _generateZipFromDocuments(params: {
		institution: string
		testMode?: boolean
		type: 'package' | 'degree'
		getDocuments: () => Promise<any>
		generateFileName: (documents: any) => string
	}): Promise<any> {
		try {
			this._validateZipParams(params)

			const documents = await params.getDocuments()
			const zip = new JSZip()

			this._addDocumentsToZip(zip, documents, params.type)

			const b64Str = await zip.generateAsync({ type: 'base64' })
			const fileName = params.generateFileName(documents)

			this._createDownloadLink('data:application/zip;base64,' + b64Str, fileName)

			return { success: true, fileName }
		} catch (error: any) {
			console.error(`Error en _generateZipFromDocuments (${params.type}):`, error)
			throw new Error(error.message ?? `Error al generar el archivo ZIP del ${params.type}`)
		}
	}

	/**
	 * @description Valida los parámetros para generar ZIP
	 */
	private _validateZipParams(params: { institution: string; type: 'package' | 'degree'; degree?: string }): void {
		if (!params.institution) {
			throw new Error('Se requiere el ID de la institución')
		}

		if (params.type === 'degree' && !params.degree) {
			throw new Error('Se requiere el ID del título')
		}
	}

	/**
	 * @description Obtiene los documentos de un paquete
	 */
	private async _getPackageDocuments(packageId: number, institutionId: string, testMode: boolean = false): Promise<any> {
		const path = this._buildFirebasePath(institutionId, testMode)
		const degreesRef = dbRef(database, path)

		try {
			const snapshot = await get(query(degreesRef, orderByChild('package'), equalTo(packageId)))

			if (!snapshot.exists()) {
				throw new Error('No se encontraron títulos para el paquete especificado')
			}

			return snapshot.val()
		} catch (dbError: any) {
			if (dbError.code === ETipoError.PERMISSION_DENIED) {
				throw new Error('No tienes permisos para acceder a estos títulos. Por favor, verifica tus credenciales.')
			}
			throw dbError
		}
	}

	/**
	 * @description Obtiene un documento de título específico
	 */
	private async _getDegreeDocument(degreeId: string, institutionId: string, testMode: boolean = false): Promise<any> {
		const path = this._buildFirebasePath(institutionId, testMode)
		const degreeRef = dbRef(database, `${path}/${degreeId}`)

		try {
			const snapshot = await get(degreeRef)

			if (!snapshot.exists()) {
				throw new Error('No se encontró el título especificado')
			}

			const document = snapshot.val()
			if (!document.folioControl || !document.cadenaOriginal || !document.xml) {
				throw new Error('El título no tiene todos los documentos necesarios (folio, cadena original o XML)')
			}

			return { [degreeId]: document }
		} catch (dbError: any) {
			if (dbError.code === ETipoError.PERMISSION_DENIED) {
				throw new Error('No tienes permisos para acceder a este título. Por favor, verifica tus credenciales.')
			}
			throw dbError
		}
	}

	/**
	 * @description Agrega documentos al archivo ZIP
	 */
	private _addDocumentsToZip(zip: JSZip, documents: any, type: 'package' | 'degree'): void {
		Object.values(documents).forEach((document: any) => {
			if (!document.folioControl || !document.cadenaOriginal || !document.xml) {
				if (type === 'package') {
					console.warn('Documento incompleto encontrado:', document)
					return
				} else {
					throw new Error('El título no tiene todos los documentos necesarios (folio, cadena original o XML)')
				}
			}

			const name = document.folioControl

			// Validar y convertir cadenaOriginal
			const cadenaOriginal = this._validateAndConvertString(document.cadenaOriginal, 'cadena original')
			zip.file('COs/' + name + '.txt', cadenaOriginal)

			// Validar y convertir XML
			const xml = this._validateAndConvertString(document.xml, 'XML')
			zip.file('XMLs/' + name + '.xml', xml)

			this._validateDocumentSello(xml, name)
		})
	}

	/**
	 * @description Valida que el documento tenga sello
	 */
	private _validateDocumentSello(xml: string, documentName: string): void {
		const parser = new DOMParser()
		const xmlDoc = parser.parseFromString(xml, 'text/xml')
		const sello = xmlDoc.getElementsByTagName('Dec')[0]?.getAttribute('sello')

		if (!sello) {
			console.warn(`Documento ${documentName} no tiene sello`)
		}
	}

	/**
	 * @description Genera el nombre del archivo para un paquete
	 */
	private _generatePackageFileName(documents: any, name?: string): string {
		return (name ?? documents.label ?? 'paquete').replace(/\s/gi, '') + new Date().toISOString().replace(/[-:]/g, '').slice(0, 8) + '.zip'
	}

	/**
	 * @description Genera el nombre del archivo para un título individual
	 */
	private _generateDegreeFileName(documents: any): string {
		const document = Object.values(documents)[0] as any
		const name = document.folioControl
		return `titulo_${name}_${new Date().toISOString().replace(/[-:]/g, '').slice(0, 8)}.zip`
	}

	/**
	 * @description Maneja llamadas a la API con manejo de errores centralizado
	 */
	private async _handleApiCall<T>(apiCall: () => Promise<T>, methodName: string): Promise<T> {
		try {
			return await apiCall()
		} catch (error) {
			console.error(`Error en ${methodName}:`, error)
			throw error
		}
	}

	/**
	 * @description Construye la ruta de Firebase con soporte para testMode
	 */
	private _buildFirebasePath(institutionId: string, testMode: boolean = false): string {
		return `/institutions/${institutionId}${testMode ? '/testMode' : ''}/degrees/queue`
	}

	/**
	 * @description Construye la query de búsqueda según el tipo
	 */
	private _buildSearchQuery(degreesRef: any, type: 'folio' | 'numero' | 'curp', value: string) {
		switch (type) {
			case 'folio':
				return query(degreesRef, orderByChild('folioControl'), equalTo(value))
			case 'numero':
				return query(degreesRef, orderByChild('data/numeroControl'), equalTo(value))
			case 'curp':
				return query(degreesRef, orderByChild('data/Profesionista/curp'), equalTo(value))
			default:
				throw new Error('Tipo de búsqueda no permitido')
		}
	}

	/**
	 * @description Mapea los resultados de Firebase a un formato consistente
	 */
	private _mapFirebaseResults(snapshot: any): any[] {
		if (snapshot.exists()) {
			console.log('busqueda result ', snapshot.val())
			return Object.entries(snapshot.val()).map(([id, data]: [string, any]) => ({
				id,
				...data
			}))
		}
		return []
	}

	/**
	 * @description Crea y descarga un archivo
	 */
	private _createAndDownloadFile(response: any, filename: string, mimeType: string): Blob {
		const blob = new Blob([response], { type: mimeType })
		this._createDownloadLink(window.URL.createObjectURL(blob), filename)
		return blob
	}

	/**
	 * @description Crea un enlace temporal para descarga
	 */
	private _createDownloadLink(href: string, filename: string): void {
		const link = document.createElement('a')
		link.href = href
		link.download = filename
		document.body.appendChild(link)
		link.click()
		document.body.removeChild(link)
		window.URL.revokeObjectURL(link.href)
	}

	/**
	 * @description Valida los permisos del usuario para la institución
	 */
	private async _validateUserPermissions(institution: number): Promise<void> {
		const auth = getAuth()
		const user = auth.currentUser
		if (!user) {
			throw new Error('Usuario no autenticado')
		}

		const userInstitutionRef = dbRef(database, `/users/${user.uid}/institutions/${institution}`)
		console.log('userInstitutionRef', userInstitutionRef)
		const userInstitutionSnapshot = await get(userInstitutionRef)
		if (!userInstitutionSnapshot.exists() || !userInstitutionSnapshot.val()) {
			throw new Error('No tienes permisos para esta institución')
		}
	}

	/**
	 * @description Valida que el título no esté en un paquete
	 */
	private async _validateDegreeNotInPackage(path: string): Promise<void> {
		const degreeRef = dbRef(database, path)
		const snapshot = await get(degreeRef)
		if (snapshot.exists() && snapshot.val().package) {
			throw new Error('No se puede modificar un titulo que ya está en un paquete')
		}
	}

	/**
	 * @description Valida y convierte un valor a string para JSZip
	 * @param value Valor a validar y convertir
	 * @param fieldName Nombre del campo para mensajes de error
	 * @returns String válido para JSZip
	 */
	private _validateAndConvertString(value: any, fieldName: string): string {
		if (!value) {
			throw new Error(`El campo ${fieldName} está vacío o es nulo`)
		}

		// Si ya es string, validar que no esté vacío
		if (typeof value === 'string') {
			if (value.trim() === '') {
				throw new Error(`El campo ${fieldName} está vacío`)
			}
			return value
		}

		// Si es un objeto, intentar convertirlo a string
		if (typeof value === 'object') {
			try {
				const stringValue = JSON.stringify(value)
				if (stringValue === '{}' || stringValue === '[]') {
					throw new Error(`El campo ${fieldName} es un objeto vacío`)
				}
				return stringValue
			} catch {
				throw new Error(`No se pudo convertir el campo ${fieldName} a string`)
			}
		}

		// Para otros tipos, convertir a string
		const stringValue = String(value)
		if (stringValue === 'undefined' || stringValue === 'null') {
			throw new Error(`El campo ${fieldName} tiene un valor inválido`)
		}

		return stringValue
	}
}

// Crear y exportar una instancia del servicio
export const degreeService = new DegreeService()
